import React, { useMemo } from 'react'
import clsx from 'clsx'

export interface ProgressRingProps {
  progress: number        // 0-100
  size?: 'sm' | 'md' | 'lg' | 'xl'
  color?: 'orange' | 'green' | 'blue' | 'red'
  animated?: boolean
  label?: string
  showPercentage?: boolean
  thickness?: 'thin' | 'normal' | 'thick'
  className?: string
}

const useReducedMotion = () =>
  useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches, [])

const ProgressRing: React.FC<ProgressRingProps> = ({
  progress,
  size = 'md',
  color = 'orange',
  animated = false,
  label,
  showPercentage = true,
  thickness = 'normal',
  className
}) => {
  const reducedMotion = useReducedMotion()
  const sizes = {
    sm: { diameter: 48, stroke: 4, fontSize: 'text-xs' },
    md: { diameter: 64, stroke: 6, fontSize: 'text-sm' },
    lg: { diameter: 80, stroke: 8, fontSize: 'text-base' },
    xl: { diameter: 120, stroke: 10, fontSize: 'text-lg' }
  }
  
  const colors = {
    orange: { ring: '#FF6B35', bg: 'rgba(255, 107, 53, 0.1)' },
    green: { ring: '#4CAF50', bg: 'rgba(76, 175, 80, 0.1)' },
    blue: { ring: '#42A5F5', bg: 'rgba(66, 165, 245, 0.1)' },
    red: { ring: '#E94560', bg: 'rgba(233, 69, 96, 0.1)' }
  }

  const strokeWidthMap = {
    thin: Math.max(2, sizes[size].stroke - 2),
    normal: sizes[size].stroke,
    thick: sizes[size].stroke + 2
  }

  const { diameter, fontSize } = sizes[size]
  const strokeWidth = strokeWidthMap[thickness]
  const radius = (diameter - strokeWidth) / 2
  const circumference = radius * Math.PI * 2
  const dash = (progress * circumference) / 100

  return (
    <div className={clsx('relative inline-flex items-center justify-center', className)}>
      <svg 
        width={diameter} 
        height={diameter} 
        className="transform -rotate-90"
        style={{ filter: animated && color === 'orange' ? 'drop-shadow(0 0 8px rgba(255, 107, 53, 0.4))' : undefined }}
      >
        {/* Background ring */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          stroke={colors[color].bg}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress ring — CSS transition */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          stroke={colors[color].ring}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - dash}
          style={{
            transition: (animated && !reducedMotion) ? 'stroke-dashoffset 1s ease-out' : 'none',
          }}
        />
      </svg>
      
      {/* Center content */}
      <div className={clsx('absolute inset-0 flex flex-col items-center justify-center', fontSize)}>
        {showPercentage && (
          <span className="font-bold text-white">
            {Math.round(progress)}%
          </span>
        )}
        {label && (
          <span className="text-slate-400 text-xs mt-1">{label}</span>
        )}
      </div>
    </div>
  )
}

export default ProgressRing
